
import { Home, Users, Wifi, Tv, Car, Waves, Thermometer, Snowflake } from "lucide-react";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";
import { useReveal } from "@/hooks/useReveal";
import { useState, useEffect, useRef } from "react";

function useStaggerReveal(count: number, threshold = 0.08) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, visible };
}

const VillaFeatures = () => {
  const featuresData = [
    { icon: <Home className="h-6 w-6" strokeWidth={1.5} />, title: "Villa Spacieuse", description: "140m² de surface habitable pour un confort optimal" },
    { icon: <Users className="h-6 w-6" strokeWidth={1.5} />, title: "Capacité", description: "Accueille jusqu'à 8 personnes confortablement" },
    { icon: <Snowflake className="h-6 w-6" strokeWidth={1.5} />, title: "Piscine Privée", description: "Piscine chauffée sur une terrasse ensoleillée" },
    { icon: <Waves className="h-6 w-6" strokeWidth={1.5} />, title: "Proche de la Plage", description: "À 5 minutes à pied des plages de sable fin" },
    { icon: <Wifi className="h-6 w-6" strokeWidth={1.5} />, title: "Wifi Haut Débit", description: "Connexion internet fibre dans toute la villa" },
    { icon: <Tv className="h-6 w-6" strokeWidth={1.5} />, title: "Divertissement", description: "TV écran plat, système audio et jeux de société" },
    { icon: <Thermometer className="h-6 w-6" strokeWidth={1.5} />, title: "Climatisation", description: "Climatisation dans toutes les chambres" },
    { icon: <Car className="h-6 w-6" strokeWidth={1.5} />, title: "Stationnement", description: "Places de parking privées devant la villa" },
  ];

  const sectionContent = { heading: "Les Prestations de la Villa" };
  const { translatedContent: sectionT } = useTranslatedContent(sectionContent);

  const featuresToTranslate = featuresData.reduce((acc, f, i) => {
    acc[`title_${i}`] = f.title;
    acc[`desc_${i}`] = f.description;
    return acc;
  }, {} as Record<string, string>);

  const { translatedContent, isLoading } = useTranslatedContent(featuresToTranslate);
  const translatedFeatures = featuresData.map((f, i) => ({
    ...f,
    title: translatedContent[`title_${i}`] || f.title,
    description: translatedContent[`desc_${i}`] || f.description,
  }));

  const headerReveal = useReveal<HTMLDivElement>();
  const stagger = useStaggerReveal(featuresData.length);

  return (
    <section className="bg-villa-offwhite overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-32">

        <div
          ref={headerReveal.ref}
          className={`text-center mb-16 reveal ${headerReveal.revealed ? "in" : ""}`}
        >
          <p className="eyebrow mb-4">Équipements</p>
          <h2 className="heading-lg text-villa-text">
            {sectionT.heading || "Les Prestations de la Villa"}
          </h2>
          <p className="mt-4 body-lg max-w-xl mx-auto">
            Tout le confort premium pour des vacances inoubliables en Méditerranée.
          </p>
        </div>

        <div
          ref={stagger.ref}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {translatedFeatures.map((feature, i) => (
            <div
              key={i}
              className="card-feature group"
              style={{
                opacity: stagger.visible ? 1 : 0,
                transform: stagger.visible ? "none" : "translateY(28px) scale(0.96)",
                transition: `opacity 0.55s ease, transform 0.55s cubic-bezier(0.16,1,0.3,1)`,
                transitionDelay: stagger.visible ? `${i * 55}ms` : "0ms",
              }}
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-villa-turquoise/8 text-villa-turquoise mb-4
                              group-hover:bg-villa-turquoise group-hover:text-white transition-all duration-300">
                {feature.icon}
              </div>
              {isLoading ? (
                <>
                  <div className="h-4 w-28 bg-black/6 rounded-lg mb-2 animate-pulse" />
                  <div className="h-10 w-full bg-black/4 rounded-lg animate-pulse" />
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-villa-text mb-1.5 text-[15px]">{feature.title}</h3>
                  <p className="text-sm text-villa-text-secondary leading-relaxed">{feature.description}</p>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VillaFeatures;
